import { ScreenOrientation } from '@signage-player/data-store';
import { getKnownInstances, peekUrlDisplayId, peekUrlInstanceId } from '@signage-player/device-browser';

export type Rotation = 0 | 90 | 180 | 270;
const ROTATIONS: readonly Rotation[] = [0, 90, 180, 270];

interface DisplayLike {
  id: number;
  orientation?: string | null;
}

let current: Rotation = 0;

/**
 * Page rotation driven by the dashboard's display orientation — the browser
 * counterpart of the xrandr rotation Electron devices apply to their output.
 * Applied from the cached device record at boot (so the loader already sits
 * rotated) and from every device sync through the screen stub.
 */
export function installWebRotation(): void {
  injectStyles();
  const id = peekUrlInstanceId();
  const inst = id ? getKnownInstances().find(k => k.id === id) : undefined;
  applyDeviceRotation(inst?.displays ?? []);
}

/** The display this tab renders: the URL segment when the device has it, else the first. */
export function boundDisplay<T extends DisplayLike>(displays: readonly T[]): T | null {
  const desired = peekUrlDisplayId();
  return displays.find(d => d.id === desired) ?? displays[0] ?? null;
}

export function applyDeviceRotation(displays: readonly DisplayLike[]): void {
  const rotation = orientationToRotation(boundDisplay(displays)?.orientation);
  current = rotation;
  for (const r of ROTATIONS) document.body.classList.toggle(`web-rot-${r}`, r !== 0 && r === rotation);
  document.documentElement.classList.toggle('web-rotated', rotation !== 0);
}

export function getRotation(): Rotation {
  return current;
}

/** Same mapping as AbstractScreenService.getRotateLinuxName; xrandr right = 90° clockwise. */
export function orientationToRotation(orientation: string | null | undefined): Rotation {
  switch (orientation) {
    case 'vertical_right':
      return 90;
    case 'upside_down':
      return 180;
    case 'vertical_left':
      return 270;
    default:
      return 0;
  }
}

export function rotationToOrientation(rotation: Rotation = current): ScreenOrientation {
  switch (rotation) {
    case 90:
      return ScreenOrientation.RIGHT;
    case 180:
      return ScreenOrientation.INVERTED;
    case 270:
      return ScreenOrientation.LEFT;
    default:
      return ScreenOrientation.NORMAL;
  }
}

/** Viewport as the content sees it: width/height swap under 90° / 270°. */
export function effectiveViewport(rotation: Rotation = current): { width: number; height: number } {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const h = typeof window !== 'undefined' ? window.innerHeight : 1080;
  return rotation === 90 || rotation === 270 ? { width: h, height: w } : { width: w, height: h };
}

function injectStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    /* The body becomes the rotated screen. Fixed-position chrome and the
       fullscreen containers follow it because a transformed ancestor is the
       containing block for fixed descendants; viewport-unit sizes swap to 100%. */
    html.web-rotated { overflow: hidden; }
    body.web.web-rot-90, body.web.web-rot-180, body.web.web-rot-270 {
      position: absolute; top: 50%; left: 50%; margin: 0; overflow: hidden; }
    body.web.web-rot-180 { width: 100vw; height: 100vh; transform: translate(-50%, -50%) rotate(180deg); }
    body.web.web-rot-90 { width: 100vh; height: 100vw; transform: translate(-50%, -50%) rotate(90deg); }
    body.web.web-rot-270 { width: 100vh; height: 100vw; transform: translate(-50%, -50%) rotate(270deg); }
    body.web.web-rot-90 .main, body.web.web-rot-90 .main > *, body.web.web-rot-90 .device, body.web.web-rot-90 .audio-icon,
    body.web.web-rot-270 .main, body.web.web-rot-270 .main > *, body.web.web-rot-270 .device, body.web.web-rot-270 .audio-icon {
      width: 100%; height: 100%; }
  `;
  document.head.appendChild(style);
}
